import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const CURRENT_PERIOD = (() => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
})();

const FALLBACK_PALIERS = [
  { pct: 70, bonus: 2 }, { pct: 75, bonus: 3 }, { pct: 80, bonus: 4 },
  { pct: 85, bonus: 5 }, { pct: 90, bonus: 7 }, { pct: 95, bonus: 8 },
  { pct: 100, bonus: 9 }, { pct: 105, bonus: 10 }, { pct: 110, bonus: 11 },
  { pct: 115, bonus: 12 }, { pct: 120, bonus: 13 }, { pct: 125, bonus: 14 },
  { pct: 130, bonus: 15 },
];

const fmt = (n) => n != null ? Math.round(n).toLocaleString('fr-FR') : '—';
const fmtUSD = (n) => n != null ? '$' + Math.round(n).toLocaleString('fr-FR') : '—';

const getCurrentPalier = (currentPct, paliers) => {
  if (currentPct == null || !paliers?.length || currentPct < paliers[0]?.pct) return null;
  let current = null;
  for (const p of paliers) {
    if (currentPct >= p.pct) current = p;
    else break;
  }
  return current;
};

const getNextPalier = (currentPct, paliers) => {
  if (currentPct == null || !paliers?.length) return paliers?.[0] || null;
  for (const p of paliers) {
    if (currentPct < p.pct) return p;
  }
  return null;
};

const fetchAllDiamondsForServer = async (server, month) => {
  const { data } = await supabase.from('creator_current_month').select('diamonds').eq('server', server);
  if (!data || data.length === 0) return 0;
  return data.reduce((sum, r) => sum + (parseInt(r.diamonds) || 0), 0);
};

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M9.5 2.5L11.5 4.5L4.5 11.5H2.5V9.5L9.5 2.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 4L10 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

const RevenueTargets = ({ servers, showToast }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('');
  const [serverData, setServerData] = useState({});
  const [targets, setTargets] = useState({});
  // ✅ FIX: paliers par serveur (objet) au lieu d'un tableau global
  const [paliersMap, setPaliersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingServer, setEditingServer] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [loadedTabs, setLoadedTabs] = useState({});
  const [showPaliersModal, setShowPaliersModal] = useState(false);
  const [editPaliers, setEditPaliers] = useState([]);
  const [savingPaliers, setSavingPaliers] = useState(false);

  useEffect(() => {
    if (servers?.length > 0 && !activeTab) {
      setActiveTab(servers[0].id);
    }
  }, [servers, activeTab]);

  const fetchTargets = useCallback(async () => {
    const { data, error } = await supabase.from('incremental_config').select('*');
    if (data && !error) {
      const targetsMap = {};
      const newPaliersMap = {};
      data.forEach(row => {
        if (row.period === CURRENT_PERIOD) {
          targetsMap[row.server] = row.target_100pct;
          if (row.completion_tiers && Array.isArray(row.completion_tiers) && row.completion_tiers.length > 0) {
            newPaliersMap[row.server] = row.completion_tiers;
          }
        }
      });
      setTargets(targetsMap);
      // ✅ FIX: stocker les paliers par serveur
      setPaliersMap(newPaliersMap);
    }
  }, [servers]);

  // ✅ FIX: paliers du serveur actif, avec fallback
  const activePaliers = paliersMap[activeTab] || FALLBACK_PALIERS;

  const getTarget = useCallback((server) => {
    return targets[server] ?? 0;
  }, [targets]);

  const fetchTabData = useCallback(async (server) => {
    if (loadedTabs[server]) return;
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const totalDiamonds = await fetchAllDiamondsForServer(server, month);
    setServerData(prev => ({ ...prev, [server]: { totalDiamonds } }));
    setLoadedTabs(prev => ({ ...prev, [server]: true }));
  }, [loadedTabs]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchTargets();
      if (servers?.length > 0) {
        const firstServer = servers[0].id;
        const now = new Date();
        const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const totalDiamonds = await fetchAllDiamondsForServer(firstServer, month);
        setServerData({ [firstServer]: { totalDiamonds } });
        setLoadedTabs({ [firstServer]: true });
      }
      setLoading(false);
    };
    if (servers?.length > 0) init();
  }, [servers]);

  useEffect(() => {
    if (!loading && activeTab) {
      fetchTabData(activeTab);
    }
  }, [activeTab, loading, fetchTabData]);

  const handleEditClick = (server, currentTarget) => {
    setEditingServer(server);
    setEditValue(String(currentTarget));
    setSaveError(null);
  };

  const handleCancelEdit = () => {
    setEditingServer(null);
    setEditValue('');
    setSaveError(null);
  };

  // ✅ FIX: UPDATE uniquement target_100pct sans écraser completion_tiers
  const handleSave = async () => {
    const val = parseInt(editValue.replace(/\s/g, '').replace(/,/g, ''), 10);
    if (isNaN(val) || val <= 0) {
      setSaveError(t('admin.config.invalidValue'));
      return;
    }
    setSaving(true);
    setSaveError(null);

    // Récupère les paliers existants pour ne pas les écraser
    const existingPaliers = paliersMap[editingServer] || FALLBACK_PALIERS;

    const { error } = await supabase.from('incremental_config').upsert(
      {
        server: editingServer,
        server_id: editingServer,
        target_100pct: val,
        period: CURRENT_PERIOD,
        completion_tiers: existingPaliers,
      },
      { onConflict: 'server,period' }
    );

    if (error) {
      setSaveError(error.message);
      setSaving(false);
      return;
    }
    await fetchTargets();
    setSaving(false);
    setEditingServer(null);
    setEditValue('');
    showToast(t('admin.config.targetUpdated'), 'success');
  };

  const handleOpenPaliers = () => {
    // ✅ FIX: ouvre les paliers du serveur actif
    setEditPaliers((paliersMap[activeTab] || FALLBACK_PALIERS).map(p => ({ ...p })));
    setShowPaliersModal(true);
  };

  // ✅ FIX: sauvegarde les paliers uniquement pour le serveur actif
  const handleSavePaliers = async () => {
    setSavingPaliers(true);
    const sorted = [...editPaliers].sort((a, b) => a.pct - b.pct);
    const currentTarget = targets[activeTab] ?? 0;

    const { error } = await supabase.from('incremental_config').upsert(
      {
        server: activeTab,
        server_id: activeTab,
        period: CURRENT_PERIOD,
        target_100pct: currentTarget,
        completion_tiers: sorted,
      },
      { onConflict: 'server,period' }
    );

    setSavingPaliers(false);
    if (error) {
      showToast(error.message, 'error');
    } else {
      setPaliersMap(prev => ({ ...prev, [activeTab]: sorted }));
      showToast(t('admin.config.paliersUpdated', { server: activeTab }), 'success');
      setShowPaliersModal(false);
    }
  };

  const activeServerObj = servers?.find(s => s.id === activeTab);
  const sd = serverData[activeTab];
  const target = getTarget(activeTab);
  const totalDiamonds = sd?.totalDiamonds ?? null;
  const currentPct = target > 0 && totalDiamonds != null ? (totalDiamonds / target) * 100 : null;
  const currentPalier = currentPct != null ? getCurrentPalier(currentPct, activePaliers) : null;
  const nextPalier = currentPct != null ? getNextPalier(currentPct, activePaliers) : null;
  const color = activeServerObj?.color ?? '#f97316';

  const diamandsManquants = nextPalier && totalDiamonds != null
    ? Math.max(0, Math.round((nextPalier.pct / 100) * target) - totalDiamonds)
    : null;

  const progressBarPct = currentPct != null ? Math.min((currentPct / 130) * 100, 100) : 0;
  const maxPalier = activePaliers[activePaliers.length - 1];
  const currentPalierProjection = currentPalier
    ? Math.round((Math.round((currentPalier.pct / 100) * target) / 100) * (currentPalier.bonus / 100))
    : 0;
  const maxProjection = maxPalier
    ? Math.round((Math.round((maxPalier.pct / 100) * target) / 100) * (maxPalier.bonus / 100))
    : 0;

  return (
    <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
      {/* Header */}
      <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: '#1f1f1f' }}>
        <div>
          <h2 className="text-white font-semibold text-base" style={{ fontFamily: 'Sora, sans-serif' }}>
            {t('admin.config.revenueTargetsTitle')}
          </h2>
          <p className="text-white/40 text-xs mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
            {t('admin.config.revenueTargetsSubtitle', { period: CURRENT_PERIOD })}
          </p>
        </div>
        <button
          onClick={handleOpenPaliers}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
          style={{ backgroundColor: '#ffffff10', color: 'rgba(255,255,255,0.6)', border: '1px solid #2a2a2a', fontFamily: 'Inter, sans-serif' }}
        >
          <PencilIcon /> {t('admin.config.editPaliers', { server: activeTab })}
        </button>
      </div>

      {/* Server Tabs */}
      <div className="flex gap-1 px-6 pt-4 pb-0 items-center flex-wrap">
        {servers?.map(({ id, color: c }) => {
          const isActive = activeTab === id;
          return (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className="px-4 py-2 rounded-t-lg text-xs font-bold transition-all"
              style={{
                fontFamily: 'Sora, sans-serif',
                backgroundColor: isActive ? `${c}22` : 'transparent',
                color: isActive ? c : 'rgba(255,255,255,0.35)',
                borderBottom: isActive ? `2px solid ${c}` : '2px solid transparent',
              }}
            >
              {id}
            </button>
          );
        })}
      </div>

      <div className="px-6 pb-6 pt-4 space-y-6">
        {/* Progress Card */}
        <div className="rounded-xl p-5 border" style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f' }}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-white/30 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.loading')}</span>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-white/40 text-xs mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {t('admin.config.targetDiamonds')}
                  </p>
                  {editingServer === activeTab ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="text"
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        className="rounded-lg px-3 py-1.5 text-base font-bold text-white outline-none border"
                        style={{ backgroundColor: '#1a1a1a', borderColor: color, fontFamily: 'Sora, sans-serif', width: '160px' }}
                        placeholder="Ex: 800000"
                        autoFocus
                      />
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                        style={{ backgroundColor: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e40', fontFamily: 'Sora, sans-serif', opacity: saving ? 0.6 : 1 }}
                      >
                        {saving ? t('admin.config.savingLabel') : t('admin.config.saveLabel')}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2.5 rounded-lg text-sm font-semibold"
                        style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('admin.config.cancel')}
                      </button>
                      {saveError && (
                        <p className="text-xs w-full mt-1" style={{ color: '#ef4444', fontFamily: 'Inter, sans-serif' }}>{saveError}</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="text-xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                        💎 {fmt(target)}
                      </p>
                      <button
                        onClick={() => handleEditClick(activeTab, target)}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all hover:opacity-80"
                        style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}30`, fontFamily: 'Inter, sans-serif' }}
                      >
                        <PencilIcon /> {t('admin.config.edit')}
                      </button>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-white/40 text-xs mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.completion')}</p>
                  <p
                    className="text-2xl font-bold"
                    style={{
                      color: currentPct != null && currentPct >= 100 ? '#22c55e' : currentPct != null && currentPct >= 70 ? color : '#ef4444',
                      fontFamily: 'Sora, sans-serif',
                    }}
                  >
                    {currentPct != null ? currentPct.toFixed(1) + '%' : '—'}
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-white/40 text-xs mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.totalDiamondsMonth')}</p>
                <p className="text-2xl font-bold" style={{ color, fontFamily: 'Sora, sans-serif' }}>
                  💎 {fmt(totalDiamonds)}
                </p>
              </div>

              <div className="mb-5">
                <div className="w-full h-3 rounded-full overflow-hidden" style={{ backgroundColor: '#1f1f1f' }}>
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${progressBarPct}%`, backgroundColor: color }} />
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-white/25 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>0%</span>
                  <span className="text-white/25 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>130%</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[140px] rounded-lg px-4 py-3 border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                  <p className="text-white/40 text-xs mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.tierReached')}</p>
                  {currentPalier ? (
                    <p className="font-bold text-sm" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>
                      ✅ {currentPalier.pct}% → Bonus {currentPalier.bonus}%
                    </p>
                  ) : (
                    <p className="text-white/30 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.noTierReached')}</p>
                  )}
                </div>
                <div className="flex-1 min-w-[140px] rounded-lg px-4 py-3 border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                  <p className="text-white/40 text-xs mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.nextTier')}</p>
                  {nextPalier ? (
                    <div>
                      <p className="font-bold text-sm" style={{ color: '#f97316', fontFamily: 'Sora, sans-serif' }}>
                        → {nextPalier.pct}% (Bonus {nextPalier.bonus}%)
                      </p>
                      <p className="text-white/40 text-xs mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {fmt(diamandsManquants)} {t('admin.config.missingDiamonds')}
                      </p>
                    </div>
                  ) : (
                    <p className="font-bold text-sm" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>{t('admin.config.allTiersReachedConfig')}</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Paliers Table */}
        <div>
          <p className="text-white/40 text-xs mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
            {t('admin.config.tierGrid', { server: activeTab })}
          </p>
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#1f1f1f' }}>
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: '#0d0d0d', borderBottom: '1px solid #1f1f1f' }}>
                  {[t('admin.config.colPalierPct'), t('admin.config.colDiamondsRequired'), t('admin.config.colBonusPct'), t('admin.config.colProjection'), t('admin.config.colStatus')].map(col => (
                    <th key={col} className="text-left py-2.5 px-4 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activePaliers.map((p, i) => {
                  const diamondsRequis = Math.round((p.pct / 100) * target);
                  const projectionUSD = Math.round((diamondsRequis / 100) * (p.bonus / 100));
                  const isAtteint = currentPct != null && currentPct >= p.pct;
                  const isProchain = !isAtteint && nextPalier != null && p.pct === nextPalier.pct;

                  let statut, statutColor, rowBg;
                  if (isAtteint) { statut = t('admin.config.statusReached'); statutColor = '#22c55e'; rowBg = `${color}0d`; }
                  else if (isProchain) { statut = t('admin.config.statusNext'); statutColor = '#f97316'; rowBg = '#f9731608'; }
                  else { statut = t('admin.config.statusLocked'); statutColor = 'rgba(255,255,255,0.25)'; rowBg = 'transparent'; }

                  return (
                    <tr key={p.pct} style={{ backgroundColor: rowBg, borderBottom: i < activePaliers.length - 1 ? '1px solid #1a1a1a' : 'none' }}>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold" style={{ backgroundColor: isAtteint ? `${color}22` : '#1f1f1f', color: isAtteint ? color : isProchain ? '#f97316' : 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
                          {p.pct}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm" style={{ color: isAtteint ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                        💎 {fmt(diamondsRequis)}
                      </td>
                      <td className="py-3 px-4 text-sm font-semibold" style={{ color: isAtteint ? color : isProchain ? '#f97316' : 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
                        {p.bonus}%
                      </td>
                      <td className="py-3 px-4 text-sm font-semibold" style={{ color: isAtteint ? '#22c55e' : 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>
                        {fmtUSD(projectionUSD)}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs font-semibold" style={{ color: statutColor, fontFamily: 'Inter, sans-serif' }}>{statut}</span>
                        {isProchain && diamandsManquants != null && (
                          <p className="text-xs mt-0.5" style={{ color: 'rgba(249,115,22,0.6)', fontFamily: 'Inter, sans-serif' }}>
                            {fmt(diamandsManquants)} {t('admin.config.missingDiamonds')}
                          </p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="mt-3 rounded-xl px-5 py-4 border flex flex-wrap gap-6" style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f' }}>
            <div>
              <p className="text-white/40 text-xs mb-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.currentProjection')}</p>
              <p className="text-base font-bold" style={{ color: currentPalier ? '#22c55e' : 'rgba(255,255,255,0.3)', fontFamily: 'Sora, sans-serif' }}>
                {currentPalier ? fmtUSD(currentPalierProjection) : '—'}
              </p>
              {currentPalier && (
                <p className="text-white/30 text-xs mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {t('admin.config.tierBonus', { pct: currentPalier.pct, bonus: currentPalier.bonus })}
                </p>
              )}
            </div>
            <div>
              <p className="text-white/40 text-xs mb-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.maxProjection', { pct: maxPalier?.pct ?? 130 })}</p>
              <p className="text-base font-bold" style={{ color, fontFamily: 'Sora, sans-serif' }}>{fmtUSD(maxProjection)}</p>
              {maxPalier && (
                <p className="text-white/30 text-xs mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {t('admin.config.tierBonus', { pct: maxPalier.pct, bonus: maxPalier.bonus })}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Paliers Edit Modal */}
      {showPaliersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="rounded-2xl border p-6 w-full max-w-lg mx-4 max-h-[80vh] flex flex-col" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
            <h3 className="text-white font-bold text-base mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('admin.config.editPaliers', { server: activeTab })}
            </h3>
            <p className="text-white/40 text-xs mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              {t('admin.config.appliedToServer', { server: activeTab })}
            </p>
            <div className="overflow-y-auto flex-1">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                    {[t('admin.config.colPalierPct'), t('admin.config.colBonusPct')].map(col => (
                      <th key={col} className="text-left py-2 px-3 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {editPaliers.map((p, i) => (
                    <tr key={i} style={{ borderBottom: i < editPaliers.length - 1 ? '1px solid #1a1a1a' : 'none' }}>
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          value={p.pct}
                          onChange={e => {
                            const updated = [...editPaliers];
                            updated[i] = { ...updated[i], pct: parseFloat(e.target.value) || 0 };
                            setEditPaliers(updated);
                          }}
                          className="rounded px-2 py-1 text-base text-white outline-none border w-20"
                          style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
                        />
                      </td>
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          step="0.5"
                          value={p.bonus}
                          onChange={e => {
                            const updated = [...editPaliers];
                            updated[i] = { ...updated[i], bonus: parseFloat(e.target.value) || 0 };
                            setEditPaliers(updated);
                          }}
                          className="rounded px-2 py-1 text-base text-white outline-none border w-20"
                          style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={handleSavePaliers}
                disabled={savingPaliers}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold transition-all"
                style={{ backgroundColor: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e40', fontFamily: 'Sora, sans-serif', opacity: savingPaliers ? 0.6 : 1 }}
              >
                {savingPaliers ? t('admin.config.savingLabel') : t('admin.config.saveLabel')}
              </button>
              <button
                onClick={() => setShowPaliersModal(false)}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}
              >
                {t('admin.config.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueTargets;